import { useMemo } from 'react';
import { useAsyncSection, type AsyncSectionState } from '../../hooks/useAsyncSection';
import type { WesFilterSelection, WesHierarchyLevel, WesTimeGrain } from './useWesPalletStagingFilters';
import {
  fetchWesDistribution,
  fetchWesGeography,
  fetchWesOverview,
  fetchWesTrend,
  type WesApiDistribution,
  type WesApiGeography,
  type WesApiOverview,
  type WesApiTrend,
} from './wesPalletStagingApi';
import { buildWesReadRequest } from './wesPalletStagingRequest';
import type { WesStagingScope } from './wesPalletStagingStaticRows';

export interface WesOverviewSections {
  overview: AsyncSectionState<WesApiOverview>;
  distribution: AsyncSectionState<WesApiDistribution>;
  geography: AsyncSectionState<WesApiGeography>;
  trend: AsyncSectionState<WesApiTrend>;
}

interface UseWesOverviewOptions {
  applied: WesFilterSelection;
  periodWeeks: any[];
  scope: WesStagingScope;
  hierarchy: WesHierarchyLevel;
  timeGrain: WesTimeGrain;
  // Only the Overview tab loads these sections.
  enabled: boolean;
}

const FRIENDLY_ERROR = () => 'Unable to load this section. Please try again.';

// Each section requests on its own and refetches only when something it depends on changes.
export function useWesOverview({ applied, periodWeeks, scope, hierarchy, timeGrain, enabled }: UseWesOverviewOptions): WesOverviewSections {
  const requests = useMemo(
    () => ({
      overview: buildWesReadRequest(applied, periodWeeks, { scope }),
      distribution: buildWesReadRequest(applied, periodWeeks, {}),
      geography: buildWesReadRequest(applied, periodWeeks, { scope, hierarchy }),
      trend: buildWesReadRequest(applied, periodWeeks, { scope, hierarchy, timeGrain }),
    }),
    [applied, periodWeeks, scope, hierarchy, timeGrain],
  );

  const key = (result: (typeof requests)['overview']) => (result.ok ? JSON.stringify(result.request) : result.reason);
  const options = { enabled: enabled && requests.overview.ok, toFriendlyError: FRIENDLY_ERROR };

  const overview = useAsyncSection(
    () => (requests.overview.ok ? fetchWesOverview(requests.overview.request) : Promise.reject(new Error('invalid'))),
    [key(requests.overview)],
    options,
  );
  const distribution = useAsyncSection(
    () => (requests.distribution.ok ? fetchWesDistribution(requests.distribution.request) : Promise.reject(new Error('invalid'))),
    [key(requests.distribution)],
    options,
  );
  const geography = useAsyncSection(
    () => (requests.geography.ok ? fetchWesGeography(requests.geography.request) : Promise.reject(new Error('invalid'))),
    [key(requests.geography)],
    options,
  );
  const trend = useAsyncSection(
    () => (requests.trend.ok ? fetchWesTrend(requests.trend.request) : Promise.reject(new Error('invalid'))),
    [key(requests.trend)],
    options,
  );

  // A selection that cannot be sent never reaches the network; every section reports it.
  if (!requests.overview.ok) {
    const invalid = <T,>(): AsyncSectionState<T> => ({ data: null, loading: false, error: requests.overview.ok ? null : requests.overview.reason, retry: () => undefined });
    return { overview: invalid(), distribution: invalid(), geography: invalid(), trend: invalid() };
  }
  return { overview, distribution, geography, trend };
}
